import styled, { css } from 'styled-components'
import {
  StyledMultiInputContainerProps,
  MultiInputTagProps,
} from './MultiInput.types'
import { InputSize } from '../Input/Input.types'

const sizeStyles = {
  small: css`
    height: 32px;
    padding: 0 ${({ theme }) => theme.spacing.small};
    font-size: ${({ theme }) => theme.typography.fontSize.small};
  `,
  medium: css`
    height: 40px;
    padding: 0 ${({ theme }) => theme.spacing.medium};
    font-size: ${({ theme }) => theme.typography.fontSize.body};
  `,
  large: css`
    height: 48px;
    padding: 0 ${({ theme }) => theme.spacing.large};
    font-size: ${({ theme }) => theme.typography.fontSize.section};
  `,
}

const variantStyles = {
  default: css<{ $error?: boolean }>`
    background-color: ${({ theme }) => theme.colors.background.primary};
    border: 1px solid
      ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.default};

    &:hover:not(:disabled) {
      border-color: ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.light};
    }

    &:focus-within {
      border-color: ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.focus};
      box-shadow: ${(props) =>
        props.$error ? props.theme.shadows.error : props.theme.shadows.focus};
    }
  `,
  filled: css<{ $error?: boolean }>`
    background-color: ${({ theme }) => theme.colors.background.secondary};
    border: 1px solid transparent;
    border-bottom-color: ${(props) =>
      props.$error
        ? props.theme.colors.border.error
        : props.theme.colors.border.default};

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.background.tertiary};
      border-bottom-color: ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.light};
    }

    &:focus-within {
      background-color: ${({ theme }) => theme.colors.background.tertiary};
      border-bottom-color: ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.focus};
      border-bottom-width: 2px;
      padding-bottom: calc(0px - 1px);
      box-shadow: none;
    }
  `,
  outlined: css<{ $error?: boolean }>`
    background-color: transparent;
    border: 2px solid
      ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.default};

    &:hover:not(:disabled) {
      border-color: ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.light};
    }

    &:focus-within {
      border-color: ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.focus};
      box-shadow: none;
    }
  `,
}

export const MultiInputContainer = styled.div<StyledMultiInputContainerProps>`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  width: ${(props) => (props.$fullWidth ? '100%' : 'auto')};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  outline: none;
  transition: all ${({ theme }) => theme.transitions.fast};
  cursor: text;

  ${(props) => sizeStyles[props.$size || 'medium']}
  ${(props) => variantStyles[props.$variant || 'default']}
  
  ${(props) =>
    props.$hasLeftIcon &&
    css`
      padding-left: ${props.$size === 'small'
        ? '36px'
        : props.$size === 'large'
          ? '52px'
          : '44px'};
    `}
  
  ${(props) =>
    props.$hasRightIcon &&
    css`
      padding-right: ${props.$size === 'small'
        ? '36px'
        : props.$size === 'large'
          ? '52px'
          : '44px'};
    `}

  &:disabled {
    background-color: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.text.tertiary};
    cursor: not-allowed;
    opacity: 0.6;
  }

  /* Dynamic height based on content */
  min-height: ${(props) => {
    switch (props.$size) {
      case 'small':
        return '32px'
      case 'large':
        return '48px'
      default:
        return '40px'
    }
  }};
  height: auto;
  padding-top: ${({ theme }) => theme.spacing.xsmall};
  padding-bottom: ${({ theme }) => theme.spacing.xsmall};
`

export const MultiInputTag = styled.div<MultiInputTagProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: ${(props) => {
    switch (props.$size) {
      case 'small':
        return `2px ${props.theme.spacing.xsmall}`
      case 'large':
        return `4px ${props.theme.spacing.small}`
      default:
        return `3px ${props.theme.spacing.xsmall}`
    }
  }};
  background-color: ${({ theme }) => theme.colors.primary}15;
  border: 1px solid ${({ theme }) => theme.colors.primary}30;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${(props) => {
    switch (props.$size) {
      case 'small':
        return '11px'
      case 'large':
        return '13px'
      default:
        return '12px'
    }
  }};
  color: ${({ theme }) => theme.colors.text.primary};
  max-width: 200px;
  line-height: 1.2;

  /* Ensure text label takes available space but leaves room for remove button */
  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }

  /* Ensure remove button always visible */
  > button {
    flex-shrink: 0;
  }
`

export const MultiInputTagRemove = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;

  &:hover {
    background-color: ${({ theme }) => theme.colors.danger}20;
    color: ${({ theme }) => theme.colors.danger};
  }

  &:focus {
    outline: none;
    background-color: ${({ theme }) => theme.colors.danger}20;
    color: ${({ theme }) => theme.colors.danger};
  }

  svg {
    width: 10px;
    height: 10px;
  }
`

export const MultiInputField = styled.input<{ $size: InputSize }>`
  flex: 1;
  min-width: 120px;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  padding: 0;
  margin: 0;
  font-size: ${(props) => {
    switch (props.$size) {
      case 'small':
        return props.theme.typography.fontSize.small
      case 'large':
        return props.theme.typography.fontSize.section
      default:
        return props.theme.typography.fontSize.body
    }
  }};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

export const MultiInputIconWrapper = styled.div<{
  $position: 'left' | 'right'
  $size: InputSize
  $isClickable?: boolean
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: ${(props) => (props.$isClickable ? 'auto' : 'none')};
  color: ${({ theme }) => theme.colors.text.secondary};

  ${(props) =>
    props.$position === 'left'
      ? css`
          left: ${props.$size === 'small'
            ? props.theme.spacing.small
            : props.$size === 'large'
              ? props.theme.spacing.medium
              : '10px'};
        `
      : css`
          right: ${props.$size === 'small'
            ? props.theme.spacing.small
            : props.$size === 'large'
              ? props.theme.spacing.medium
              : '10px'};
        `}
`
